import React, { useState, useRef, useEffect } from "react";
import { Question } from "../../types";
import { Button } from "react-bootstrap";
import "./RolePlayPracticeBlock.scss";

interface RolePlayPracticeBlockProps {
  question: Question;
  onSubmit: (isCorrect: boolean) => void;
  isAnswered: boolean;
  showMarkScheme: boolean;
  onToggleMarkScheme: () => void;
  selectedAnswer?: boolean; // true = got it right, false = got it wrong, undefined = not answered
  onNextQuestion?: () => void;
  isLastQuestion?: boolean;
}

export const RolePlayPracticeBlock: React.FC<RolePlayPracticeBlockProps> = ({
  question,
  onSubmit,
  isAnswered,
  showMarkScheme,
  onToggleMarkScheme,
  selectedAnswer,
  onNextQuestion,
  isLastQuestion,
}) => {
  const [leftWidth, setLeftWidth] = useState(50); // percentage
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = (isCorrect: boolean) => {
    onSubmit(isCorrect);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newLeftWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;

      // Constrain between 20% and 80%
      if (newLeftWidth >= 20 && newLeftWidth <= 80) {
        setLeftWidth(newLeftWidth);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <div className="role-play-practice-block">
      <div className="content-wrapper" ref={containerRef}>
        <div className="left-column" style={{ width: `${leftWidth}%` }}>
          <div className="briefs-container">
            <div className="brief-section">
              {/* <h5 className="brief-title">Candidate Brief</h5> */}
              <div
                className="brief-content"
                dangerouslySetInnerHTML={{ __html: question.text || "" }}
              />
            </div>

            {/* <div className="brief-section">
              <h5 className="brief-title">Actor Brief</h5>
              <div
                className="brief-content"
                dangerouslySetInnerHTML={{ __html: question.actorBrief || "" }}
              />
            </div> */}
          </div>

          {showMarkScheme && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "15px" }}>
              <div style={{ display: "flex", gap: "10px" }}>
                <Button
                  variant={selectedAnswer !== undefined ? (selectedAnswer === true ? "success" : "outline-success") : "success"}
                  size="sm"
                  onClick={() => handleSubmit(true)}
                  disabled={selectedAnswer !== undefined}
                  style={{
                    fontWeight: selectedAnswer === true ? 600 : 500,
                    minWidth: "120px",
                    fontSize: "14px",
                    opacity: selectedAnswer !== undefined ? (selectedAnswer === true ? 1 : 0.5) : 1,
                  }}
                >
                  I Got It Right
                </Button>
                <Button
                  variant={selectedAnswer !== undefined ? (selectedAnswer === false ? "danger" : "outline-danger") : "danger"}
                  size="sm"
                  onClick={() => handleSubmit(false)}
                  disabled={selectedAnswer !== undefined}
                  style={{
                    fontWeight: selectedAnswer === false ? 600 : 500,
                    minWidth: "120px",
                    fontSize: "14px",
                    opacity: selectedAnswer !== undefined ? (selectedAnswer === false ? 1 : 0.5) : 1,
                  }}
                >
                  I Got It Wrong
                </Button>
              </div>
              {selectedAnswer !== undefined && onNextQuestion && !isLastQuestion && (
                <Button onClick={onNextQuestion} size="sm">
                  Next question
                </Button>
              )}
            </div>
          )}
        </div>

        <div
          className="divider"
          onMouseDown={handleMouseDown}
          style={{ cursor: isDragging ? 'col-resize' : 'col-resize' }}
        />

        <div className="right-column" style={{ width: `${100 - leftWidth}%` }}>
          {showMarkScheme ? (
            <div className="mark-scheme-section">
              <div
                className="mark-scheme-content"
                dangerouslySetInnerHTML={{ __html: question.explanation || "" }}
              />
            </div>
          ) : (
            <div className="mark-scheme-placeholder">
              <Button
                variant="primary"
                onClick={onToggleMarkScheme}
              >
                Show Mark Scheme
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
